import { backgroundOrange } from '@/theme/colors'
import Breadcrumbs from '@components/Breadcrumbs'
import OnboardingSurveyModal from '@components/OnboardingSurveyModal'
import { ShellRow } from '@components/Page'
import { useAuth } from '@context/AuthContext'
import { useBreadcrumbContext } from '@context/BreadcrumbContext'
import { useMe } from '@hooks/useMe'
import { isSurveyPending } from '@lib/onboardingSurvey'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import Header from '../Header'

const LayoutContainer = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;
	overflow: hidden;
`

/* Full-bleed cream band; the trail's gutter lives on the shared ShellRow, so its
   left edge lines up with the brand above and the page content below. */
const BreadcrumbWrapper = styled.div`
	background-color: ${backgroundOrange};
	flex-shrink: 0;
`

const ContentWrapper = styled.div`
	flex: 1;
	min-height: 0;
	display: flex;
	flex-direction: column;
`

interface LayoutProps {
	children: React.ReactNode
	showBackButton?: boolean
	backButtonText?: string
	onBackClick?: () => void
}

const Layout: React.FC<LayoutProps> = ({
	children,
	showBackButton = false,
	backButtonText = 'Back',
	onBackClick,
}) => {
	const navigate = useNavigate()
	const { logout, user: authUser } = useAuth()
	const { breadcrumbs, trailing: Trailing } = useBreadcrumbContext()
	const { data: user } = useMe()
	const isAdmin = user?.role === 'ADMIN'
	// Pending is keyed to the User created at signup and survives refreshes; the modal
	// clears it on answer or skip, so it shows at most once overall. Derived at render
	// time because the auth session hydrates in an effect after first mount.
	const [surveyDismissed, setSurveyDismissed] = React.useState(false)
	const showSurvey = !surveyDismissed && isSurveyPending(authUser?.id)

	const handleLogout = () => {
		logout()
		navigate('/')
	}

	const handleBack = () => {
		if (onBackClick) {
			onBackClick()
		} else {
			navigate(-1) // Default: go back in browser history
		}
	}

	const handleMyTripsClick = () => {
		navigate('/trips')
	}

	const handleMyProfileClick = () => {
		navigate('/profile')
	}

	const handleAdminClick = () => {
		navigate('/admin')
	}

	return (
		<LayoutContainer>
			<Header
				onLogout={handleLogout}
				showBackButton={showBackButton}
				onBackClick={handleBack}
				backButtonText={backButtonText}
				onMyTripsClick={handleMyTripsClick}
				onMyProfileClick={handleMyProfileClick}
				onAdminClick={isAdmin ? handleAdminClick : undefined}
			/>
			{breadcrumbs.length > 0 && (
				<BreadcrumbWrapper>
					<ShellRow>
						<Breadcrumbs items={breadcrumbs} trailing={Trailing ? <Trailing /> : undefined} />
					</ShellRow>
				</BreadcrumbWrapper>
			)}
			<ContentWrapper>
				{children}
			</ContentWrapper>
			<OnboardingSurveyModal isOpen={showSurvey} onClose={() => setSurveyDismissed(true)} />
		</LayoutContainer>
	)
}

export default Layout
